import React, { useEffect, useState } from "react";
import "./UnverifiedUsers.css";
import { db, auth } from "../firebase";
import { collection, getDocs } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useNavigate } from "react-router-dom";

export default function UnverifiedUsers() {
  const [loading, setLoading] = useState(true);
  const [unverifiedUsers, setUnverifiedUsers] = useState([]);
  const [userData, setUserData] = useState(null);
  const [userLoading, setUserLoading] = useState(true);
  const [processingUser, setProcessingUser] = useState(null);
  const [copiedLink, setCopiedLink] = useState(null);
  const [error, setError] = useState(null);

  const navigate = useNavigate();
  const functions = getFunctions();

  // Check admin permissions
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        try {
          const usersRef = collection(db, "users");
          const usersSnap = await getDocs(usersRef);
          
          let userDoc = null;
          usersSnap.forEach((doc) => {
            if (doc.data().uid === user.uid || doc.id === user.uid) {
              userDoc = {
                id: doc.id,
                uid: user.uid,
                ...doc.data(),
              };
            }
          });

          if (userDoc && userDoc.role === "admin") {
            setUserData(userDoc);
          } else {
            navigate("/admin");
          }
        } catch (error) {
          navigate("/admin");
        }
      } else {
        navigate("/login");
      }
      setUserLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  // Fetch unverified users
  useEffect(() => {
    if (!userLoading && userData) {
      fetchUnverifiedUsers();
    }
  }, [userLoading, userData]);

  async function fetchUnverifiedUsers() {
    try {
      setLoading(true);
      setError(null);
      const getUnverifiedUsers = httpsCallable(functions, 'getUnverifiedUsers');
      const result = await getUnverifiedUsers();
      setUnverifiedUsers(result.data.users || []);
    } catch (err) {
      setError("Failed to load unverified users: " + err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleVerifyUser(uid, email) {
    if (!window.confirm(`Manually verify ${email}? This will mark their email as verified.`)) {
      return;
    }

    try {
      setProcessingUser(uid);
      setError(null);
      const verifyUser = httpsCallable(functions, 'verifyUser');
      await verifyUser({ uid });
      
      // Remove from list
      setUnverifiedUsers(prev => prev.filter(u => u.uid !== uid));
      alert(`Successfully verified ${email}`);
    } catch (err) {
      setError("Failed to verify user: " + err.message);
    } finally {
      setProcessingUser(null);
    }
  }

  async function handleGenerateLink(uid, email) {
    try {
      setProcessingUser(uid);
      setError(null);
      const generateLink = httpsCallable(functions, 'generateVerificationLink');
      const result = await generateLink({ uid });
      
      const link = result.data.link;
      
      // Copy to clipboard
      await navigator.clipboard.writeText(link);
      setCopiedLink(uid);
      setTimeout(() => setCopiedLink(null), 3000);
      
      alert(`Verification link copied to clipboard!\n\nYou can now paste this link in an email to ${email}`);
    } catch (err) {
      setError("Failed to generate link: " + err.message);
    } finally {
      setProcessingUser(null);
    }
  }

  if (userLoading || loading) {
    return (
      <div className="unverified-page">
        <div className="unverified-loader">
          <div className="spinner"></div>
          <p>Loading unverified users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="unverified-page">
      <div className="unverified-container">
        <header className="unverified-header">
          <div className="header-top">
            <button 
              className="back-btn"
              onClick={() => navigate("/admin")}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Back to Dashboard
            </button>
            <button 
              className="refresh-btn"
              onClick={fetchUnverifiedUsers}
              disabled={loading}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M21.5 2V8M21.5 8H15.5M21.5 8L18.5 5C17.3 3.7 15.7 2.8 13.9 2.4C12.1 2 10.2 2.2 8.5 3C6.8 3.7 5.4 5 4.5 6.5C3.5 8.1 3 10 3 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2.5 22V16M2.5 16H8.5M2.5 16L5.5 19C6.7 20.3 8.3 21.2 10.1 21.6C11.9 22 13.8 21.8 15.5 21C17.2 20.3 18.6 19 19.5 17.5C20.5 15.9 21 14 21 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Refresh
            </button>
          </div>
          <h1>Unverified Users</h1>
          <p className="subtitle">
            Users who haven't verified their email addresses. You can manually verify them or generate a new verification link.
          </p>
        </header>

        {error && (
          <div className="error-banner">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            {error}
          </div>
        )}

        {unverifiedUsers.length === 0 ? (
          <div className="empty-state">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <path d="M8 14s1.5 2 4 2 4-2 4-2M9 9h.01M15 9h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <h2>All Clear!</h2>
            <p>No unverified users found. All users have verified their email addresses.</p>
          </div>
        ) : (
          <div className="users-grid">
            <div className="users-count">
              {unverifiedUsers.length} unverified user{unverifiedUsers.length !== 1 ? 's' : ''}
            </div>
            
            {unverifiedUsers.map((user) => (
              <div key={user.uid} className="user-card">
                <div className="user-info">
                  <div className="user-avatar">
                    {user.email ? user.email.charAt(0).toUpperCase() : '?'}
                  </div>
                  <div className="user-details">
                    <h3>{user.displayName || 'No Name'}</h3>
                    <p className="user-email">{user.email}</p>
                    <p className="user-meta">
                      Created: {new Date(user.metadata.creationTime).toLocaleDateString()}
                    </p>
                    <p className="user-meta">
                      Last Sign In: {user.metadata.lastSignInTime ? new Date(user.metadata.lastSignInTime).toLocaleDateString() : 'Never'}
                    </p>
                  </div>
                </div>

                <div className="user-actions">
                  <button
                    className="action-btn verify-btn"
                    onClick={() => handleVerifyUser(user.uid, user.email)}
                    disabled={processingUser === user.uid}
                  >
                    {processingUser === user.uid ? (
                      <>
                        <div className="btn-spinner"></div>
                        Processing...
                      </>
                    ) : (
                      <>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <polyline points="22 4 12 14.01 9 11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Manually Verify
                      </>
                    )}
                  </button>

                  <button
                    className="action-btn link-btn"
                    onClick={() => handleGenerateLink(user.uid, user.email)}
                    disabled={processingUser === user.uid}
                  >
                    {processingUser === user.uid ? (
                      <>
                        <div className="btn-spinner"></div>
                        Generating...
                      </>
                    ) : copiedLink === user.uid ? (
                      <>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                          <polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Copied!
                      </>
                    ) : (
                      <>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Copy Verification Link
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}